<template>
  <div>
    <div class="row mb-2">
      <div class="col-md-12 d-flex align-items-center justify-content-between">
        <h4 class="mb-0">
          <feather-icon
            icon="GitBranchIcon"
            size="20"
            class="mr-1 text-primary"
          />
          {{ $t('node.crud.title') }}
        </h4>
        <div class="d-flex align-items-center">
          <b-form-input
            v-model="search"
            :placeholder="$t('node.crud.searchPlaceholder')"
            size="sm"
            style="width: 200px"
            class="mr-1"
          />
          <b-button
            v-if="$hasAbility('node.admin')"
            variant="primary"
            size="sm"
            @click="$router.push('/nodes/add')"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-50" />
            {{ $t('node.crud.newNodeButton') }}
          </b-button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <div
      v-else-if="filteredRoots.length === 0"
      class="text-center py-5 text-muted"
    >
      <feather-icon icon="GitBranchIcon" size="48" class="mb-2" />
      <p>{{ $t('node.crud.noNodesFound') }}</p>
      <b-button
        v-if="$hasAbility('node.admin')"
        variant="primary"
        size="sm"
        @click="$router.push('/nodes/add')"
        >{{ $t('node.crud.createFirstNodeButton') }}</b-button
      >
    </div>

    <div v-else class="node-tree">
      <node-tree-item
        v-for="node in filteredRoots"
        :key="node.id"
        :node="node"
        :depth="0"
        @admin="goToAdmin"
        @edit="goToEdit"
        @delete="deleteNode"
      />
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BFormInput } from "bootstrap-vue";
import NodeTreeItem from "./NodeTreeItem.vue";

export default {
  components: { BButton, BSpinner, BFormInput, NodeTreeItem },

  data() {
    return {
      loading: true,
      allNodes: [],
      search: "",
    };
  },

  computed: {
    // Monta a árvore a partir da lista flat
    tree() {
      const map = {};
      const roots = [];

      this.allNodes.forEach((n) => {
        map[n.id] = { ...n, children: [] };
      });

      this.allNodes.forEach((n) => {
        if (n.parent_id && map[n.parent_id]) {
          map[n.parent_id].children.push(map[n.id]);
        } else {
          roots.push(map[n.id]);
        }
      });

      return roots;
    },

    filteredRoots() {
      if (!this.search.trim()) return this.tree;
      const q = this.search.toLowerCase();
      // filtra recursivamente mantendo a estrutura
      const filterTree = (nodes) =>
        nodes
          .map((n) => ({ ...n, children: filterTree(n.children || []) }))
          .filter(
            (n) =>
              n.name.toLowerCase().includes(q) ||
              (n.type && n.type.name.toLowerCase().includes(q)) ||
              n.children.length > 0,
          );
      return filterTree(this.tree);
    },
  },

  methods: {
    loadNodes() {
      this.loading = true;
      axios
        .get(
          this.$store.state.api +
            "nodes?paginated=false&with[]=type&with[]=parent",
        )
        .then((r) => {
          this.allNodes = r.data.data;
        })
        .catch(() => {
          this.$message(this.$t("node.messages.errorTitle"), this.$t("node.crud.messages.loadError"), "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    goToAdmin(node) {
      this.$router.push("/nodes/admin/" + node.id);
    },

    goToEdit(node) {
      this.$router.push("/nodes/edit/" + node.id);
    },

    deleteNode(node) {
      const self = this;
      swal({
        title: self.$t("node.crud.messages.deleteConfirmTitle"),
        text: self.$t("node.crud.messages.deleteConfirmText", { name: node.name }),
        type: "warning",
        buttons: {
          catch: { text: self.$t("node.crud.messages.no"), value: "cancel", className: "btn-danger" },
          confirm: { text: self.$t("node.crud.messages.yesDelete"), value: "confirm" },
        },
      }).then((value) => {
        if (value !== "confirm") return;
        self.$loading(true);
        axios
          .delete(self.$store.state.api + "nodes/" + node.id)
          .then(() => {
            self.$message(self.$t("node.messages.successTitle"), self.$t("node.crud.messages.nodeDeleted"), "success");
            self.$loading(false);
            self.loadNodes();
          })
          .catch((e) => {
            self.$loading(false);
            self.$message(null, e.response.data, "error");
          });
      });
    },
  },

  mounted() {
    this.loadNodes();
  },
};
</script>

<style scoped>
.node-tree {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.node-tree-item {
  position: relative;
}

.node-card {
  position: relative;
}

.node-card-inner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px 12px;
  transition: box-shadow 0.15s, border-color 0.15s;
  margin-bottom: 4px;
}

.node-card-inner:hover {
  border-color: #7367f0;
  box-shadow: 0 2px 10px rgba(115, 103, 240, 0.12);
}

/* destaque visual por profundidade */
.depth-0 .node-card-inner {
  border-left: 4px solid #7367f0;
}
.depth-1 .node-card-inner {
  border-left: 4px solid #28c76f;
}
.depth-2 .node-card-inner {
  border-left: 4px solid #00cfe8;
}
.depth-3 .node-card-inner {
  border-left: 4px solid #ff9f43;
}

.btn-toggle {
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  width: 20px;
  padding: 0;
  flex-shrink: 0;
}

.btn-toggle-placeholder {
  width: 20px;
  flex-shrink: 0;
}

.node-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.node-info {
  flex: 1;
  min-width: 0;
}

.node-name {
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.node-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  flex-wrap: wrap;
}

.badge-type {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 20px;
  font-weight: 600;
}

.meta-item {
  font-size: 0.75rem;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 4px;
}

.node-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.node-card-inner:hover .node-actions {
  opacity: 1;
}

.btn-icon {
  width: 30px;
  height: 30px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
</style>
